import type { SourceCompareReport } from "../../game/types.js";
import type { SourcePack } from "@desk/core";

export type BeatVisualStatus = "kept" | "trimmed" | "omitted" | "reordered";

export interface SourceBeatStripProps {
  pack: SourcePack;
  compare: SourceCompareReport;
  testId?: string;
}

/** Compact visual beat strip — color/status without good/bad moral labels. */
export function SourceBeatStrip({
  pack,
  compare,
  testId = "source-beat-strip",
}: SourceBeatStripProps) {
  const omitted = new Set(compare.omittedBeats.map((b) => b.id));
  const trimmed = new Set(compare.trimmedBeats.map((b) => b.id));
  const canonical = pack.beats.map((b) => b.id);
  const orderChanged = compare.reordered;

  const rows = pack.beats.map((beat, index) => {
    let status: BeatVisualStatus = "kept";
    if (omitted.has(beat.id)) status = "omitted";
    else if (trimmed.has(beat.id)) status = "trimmed";
    else if (orderChanged && compare.reorderOrder[index] !== canonical[index]) {
      status = "reordered";
    }
    // Also mark if position in reorder differs
    if (
      status === "kept" &&
      orderChanged &&
      compare.reorderOrder.indexOf(beat.id) !== index
    ) {
      status = "reordered";
    }
    return { beat, status };
  });

  return (
    <div className="source-beat-strip" data-testid={testId} role="list">
      <div className="beat-strip-legend" data-testid={`${testId}-legend`}>
        <span data-status="kept">Kept</span>
        <span data-status="trimmed">Trimmed</span>
        <span data-status="omitted">Omitted</span>
        <span data-status="reordered">Reordered</span>
      </div>
      <ol className="beat-strip-track">
        {rows.map(({ beat, status }) => (
          <li
            key={beat.id}
            role="listitem"
            className={`beat-chip beat-chip-${status}`}
            data-status={status}
            data-beat-id={beat.id}
            data-testid={`beat-chip-${beat.id}`}
            title={`${beat.label} · ${status}`}
          >
            <span className="chip-status">{status}</span>
            <strong className="beat-chip-label">{beat.label}</strong>
          </li>
        ))}
      </ol>
    </div>
  );
}
